import React from "react";
import { Link } from "react-router-dom";

const HubBanner = ({
  group,
  user,
  sharedReviewersCount = 0,
  membersCount = 0,
  messagesCount = 0,
  hasUnreadMessage = false,
  hasActiveChallenge = false,
  onCopyInviteCode,
  onOpenMembers,
  onOpenShareModal,
  onOpenChat,
  onOpenChallenge,
}) => {
  const isOwner = String(group?.owner) === String(user?._id);

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#2563EB] via-[#1D4ED8] to-[#1E40AF] p-4 md:p-5 text-white shadow-md shadow-blue-900/10">
      <div className="absolute -right-16 -top-16 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />
      <div className="absolute right-32 -bottom-16 w-36 h-36 bg-blue-400/20 rounded-full blur-xl pointer-events-none" />

      <div className="relative z-10 flex flex-col gap-3.5">
        {/* Top Navigation & Meta Row */}
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/study-group"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-100 hover:text-white bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-lg backdrop-blur-md border border-white/10 transition"
            >
              <i className="fa-solid fa-arrow-left text-[10px]"></i>
              <span>Back to Study Groups</span>
            </Link>

            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-white/15 text-white backdrop-blur-md border border-white/10">
              <i className="fa-solid fa-book-open text-[10px] text-blue-200"></i>
              {group?.subject || "General Study"}
            </span>

            {isOwner ? (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-lg bg-amber-400/30 text-amber-200 border border-amber-300/40">
                👑 Owner
              </span>
            ) : (
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-lg bg-white/10 text-blue-200 border border-white/10">
                Collaborator
              </span>
            )}
          </div>

          {/* Active Invite Code Quick Button */}
          {group?.inviteCode && group?.inviteCodeActive && (
            <button
              type="button"
              onClick={onCopyInviteCode}
              className="inline-flex items-center gap-1.5 text-xs font-mono font-bold text-white bg-white/15 hover:bg-white/25 px-2.5 py-1 rounded-lg backdrop-blur-md border border-white/15 transition shadow-xs"
              title="Click to copy invite code"
            >
              <i className="fa-solid fa-key text-[10px] text-blue-200"></i>
              <span>{group.inviteCode}</span>
              <i className="fa-solid fa-copy text-[10px] text-blue-200 ml-0.5"></i>
            </button>
          )}
        </div>

        {/* Main Row: Room Title + Inline Stats on Left, Actions on Right */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pt-0.5">
          <div className="min-w-0">
            <h1
              className="text-xl md:text-2xl font-extrabold tracking-tight truncate"
              title={group?.name || "Study Group Hub"}
            >
              {group?.name || "Study Group Hub"}
            </h1>

            {/* Compact Inline Stats Chips */}
            <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-blue-100">
              <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/10 px-2.5 py-0.5 rounded-md font-medium">
                <i className="fa-solid fa-file-lines text-[10px] text-blue-200"></i>
                <span>Shared Reviewers:</span>
                <strong className="text-white font-bold">{sharedReviewersCount}</strong>
              </span>
              <button
                type="button"
                onClick={onOpenMembers}
                className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/10 px-2.5 py-0.5 rounded-md font-medium transition cursor-pointer text-left"
                title="View study group members"
              >
                <i className="fa-solid fa-users text-[10px] text-blue-200"></i>
                <span>Room Members:</span>
                <strong className="text-white font-bold">
                  {membersCount} / {group?.maxMembers || 5}
                </strong>
              </button>
              <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/10 px-2.5 py-0.5 rounded-md font-medium">
                <i className="fa-solid fa-comments text-[10px] text-blue-200"></i>
                <span>Chat Messages:</span>
                <strong className="text-white font-bold">{messagesCount}</strong>
              </span>
            </div>
          </div>

          {/* Room Action Buttons */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onOpenMembers}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-white/15 hover:bg-white/25 border border-white/20 text-white backdrop-blur-md transition active:scale-95"
            >
              <i className="fa-solid fa-users text-xs"></i>
              <span>Members ({membersCount})</span>
            </button>

            <button
              type="button"
              onClick={onOpenChallenge}
              className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-extrabold backdrop-blur-md transition active:scale-95 ${
                hasActiveChallenge
                  ? "bg-amber-400 text-amber-950 shadow-md shadow-amber-400/20 hover:bg-amber-300 ring-2 ring-amber-300/40"
                  : "bg-white/15 hover:bg-white/25 border border-white/20 text-white"
              }`}
              title={hasActiveChallenge ? "View active challenge" : "Start a group challenge"}
            >
              <i className="fa-solid fa-bolt text-xs"></i>
              <span>{hasActiveChallenge ? "⚡ Challenge" : "Challenge"}</span>
            </button>

            <button
              type="button"
              onClick={onOpenShareModal}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-white/15 hover:bg-white/25 border border-white/20 text-white backdrop-blur-md transition active:scale-95"
            >
              <i className="fa-solid fa-file-import text-xs"></i>
              <span>Share Reviewer</span>
            </button>

            <button
              type="button"
              onClick={onOpenChat}
              className="relative inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-extrabold bg-white text-blue-700 hover:bg-blue-50 shadow-md transition active:scale-95"
            >
              <i className="fa-solid fa-comments text-xs"></i>
              <span>Group Chat</span>
              {hasUnreadMessage && (
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 ring-2 ring-white"></span>
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HubBanner;
